import axios, { AxiosError, AxiosResponse } from 'axios';
import { applicationConfig } from 'config/application_config';
import { PaginationData } from 'constants/interface';
import { ApplicationError } from 'infra/error/application';
import { UNAUTHORIZED_ERROR } from 'infra/error/errors';

interface RequestClient {
  get(url: string, params?: Record<string, unknown>): Promise<AxiosResponse>
  post(url: string, data?: Record<string, unknown>): Promise<AxiosResponse>
  put(url: string, data?: Record<string, unknown>): Promise<AxiosResponse>
}

interface PaginationParams {
  total: number
}

export interface PaginatedResponse<E> {
  pagination: PaginationParams
  data: E[]
}

const axiosClient = axios.create({
  baseURL: applicationConfig.api.HOST,
  withCredentials: true,
});

export const urlBuilder = (path: string, params?: Record<string, string>): URL => {
  const urlQueryParams = params ? `?${new URLSearchParams(params).toString()}` : '';
  const urlPath = `${path}${urlQueryParams}`;
  const url = new URL(urlPath, applicationConfig.api.HOST);
  return url;
};

export const createRequestError = (err: AxiosError): ApplicationError => {
  const {
    status,
    statusText,
  } = err.response || {};
  const appError = err.response?.status === 401
    ? new ApplicationError(UNAUTHORIZED_ERROR.keyCode)
    : new ApplicationError(String(status), statusText, err.response);

  return appError;
};

const requestClientBuilder = (): RequestClient => {
  const get = async (path: string, params?: Record<string, string>) => {
    try {
      const url = urlBuilder(path, params);
      const response = await axiosClient.get(url.toString());
      return response;
    } catch (err) {
      throw createRequestError(err as AxiosError);
    }
  };

  const post = async (path: string, data?: Record<string, unknown>) => {
    try {
      const url = urlBuilder(path);
      const response = await axiosClient.post(url.toString(), data);
      return response;
    } catch (err) {
      throw createRequestError(err as AxiosError);
    }
  };

  const put = async (path: string, data?: Record<string, unknown>) => {
    try {
      const url = urlBuilder(path);
      const response = await axiosClient.put(url.toString(), data);
      return response;
    } catch (err) {
      throw createRequestError(err as AxiosError);
    }
  };

  return {
    get,
    post,
    put,
  };
};

export const getPaginationHeaders = (result: AxiosResponse): PaginationParams => ({
  total: parseInt(result.headers['total-count'] || '', 10),
});

export const buildPaginationParams = (data: PaginationData): Record<string, unknown> => {
  const params: Record<string, unknown> = {
    ...data.filter,
    ...data,
  };
  delete params.filter;
  Object.keys(params).forEach((key) => params[key] === undefined && delete params[key]);
  return params;
};

export const requestClient = requestClientBuilder();
